import { hasPpt, ppt } from "../../../utils/formatUtils";
import { shapeTypeOf, supportsTextFrame } from "../../../utils/shapeTypes";
import { logError, scrubMessage } from "../../../../../telemetry";
import type { SelectionKind } from "../types";

const CHIPS_CONNECTOR = "deckorate";

/** Office.js `.load()` takes a comma-separated property-name string. */
const ITEMS_PROPERTY = "items";
/** `id` rides along with `type`: it costs no extra round-trip and is the cache
 *  key the image classifier needs before it decides whether to spend a call. */
const SHAPE_PROPERTIES = "type,id";

/** What {@link readSelectionKind} reports back to the dock. */
export interface SelectionSnapshot {
  readonly kind: SelectionKind;
  /** Number of shapes selected — 0 when nothing is. Drives the "N shapes" label. */
  readonly shapeCount: number;
  /**
   * Office.js id of the single selected shape; absent for an empty or
   * multi-selection. Used to cache an image classification per shape and to
   * discard one that arrives after the selection has moved on.
   */
  readonly shapeId?: string;
  /**
   * Context-banner text from the image classifier, when it has answered for this
   * shape. Absent otherwise — the dock falls back to `selectionLabel`.
   */
  readonly bannerLabel?: string;
}

/** Nothing selected: chips act on the slide. Also the safe fallback on failure. */
const SLIDE_SNAPSHOT: SelectionSnapshot = { kind: "slide", shapeCount: 0 };

/**
 * Office.js shape-type names this module branches on, named rather than inlined
 * so the classification reads as data and no literal is repeated.
 * `IMAGE`/`PICTURE` both appear because the host reports pictures under either
 * name depending on the API build.
 */
const SHAPE_TYPE = {
  TABLE: "table",
  CHART: "chart",
  IMAGE: "image",
  PICTURE: "picture",
  GROUP: "group",
} as const;

/** Classify a single selected shape into the chip set it should offer. */
function kindOfShape(shape: PowerPoint.Shape): SelectionKind {
  const type = shapeTypeOf(shape);
  if (type === SHAPE_TYPE.TABLE) return "table";
  if (type === SHAPE_TYPE.CHART) return "chart";
  if (type === SHAPE_TYPE.IMAGE || type === SHAPE_TYPE.PICTURE) return "image";
  // A group is treated as a multi-selection: its members are heterogeneous, so
  // the union chip set is the only honest offer.
  if (type === SHAPE_TYPE.GROUP) return "multi";
  // Falls back to the text chips for any unrecognised type — see the deny-list
  // rationale on NON_TEXT_SHAPE_TYPES.
  return supportsTextFrame(shape) ? "textbox" : "shape";
}

/**
 * Reads the current PowerPoint selection and classifies it into the chip set it
 * should offer.
 *
 * Deliberately does NOT fall back to "every shape on the active slide" the way
 * colourOps' `getSelectedShapesFlat()` does — with nothing selected the dock
 * must show the slide/deck chips, which is exactly what an empty selection
 * means here.
 *
 * Resolves to `slide` (rather than rejecting) whenever the host can't answer:
 * a non-PowerPoint host, an Office build without `getSelectedShapes()`
 * (pre-API-set 1.5), or a mid-call Office.js failure. The dock always has a
 * usable chip set that way, and the user never sees an empty tray.
 */
export async function readSelectionKind(): Promise<SelectionSnapshot> {
  if (!hasPpt()) return SLIDE_SNAPSHOT;

  try {
    return await ppt().run(async (context) => {
      type Selection = { load(properties: string): void; items: PowerPoint.Shape[] };
      const selection = (
        context.presentation as unknown as { getSelectedShapes(): Selection }
      ).getSelectedShapes();

      selection.load(ITEMS_PROPERTY);
      // `items` is only populated once the queued load is flushed by a sync —
      // reading it beforehand would see an unloaded proxy.
      await context.sync();

      const shapes = selection.items;
      if (shapes.length === 0) return SLIDE_SNAPSHOT;
      if (shapes.length > 1) return { kind: "multi", shapeCount: shapes.length };

      const shape = shapes[0];
      if (!shape) return SLIDE_SNAPSHOT;

      // Classifying a single shape needs its `type`, which is a second
      // load/sync round-trip on the shape itself — the same two-step
      // load("items") → load("type") shape used by colourOps.ts.
      (shape as unknown as { load(properties: string): void }).load(SHAPE_PROPERTIES);
      await context.sync();

      // `id` is typed as required on PowerPoint.Shape but is absent on older
      // API builds and on the test fakes, so it is read defensively: a missing
      // id costs the classification cache, not the chip set.
      const shapeId = (shape as unknown as { id?: unknown }).id;
      return {
        kind: kindOfShape(shape),
        shapeCount: 1,
        ...(typeof shapeId === "string" && shapeId ? { shapeId } : {}),
      };
    });
  } catch (error) {
    logError(
      "error.connector.failure",
      {
        type: "ActionChipSelectionError",
        scrubbedMessage: scrubMessage(error instanceof Error ? error.message : String(error)),
      },
      { connectorSystemName: CHIPS_CONNECTOR, reason: "action-chip-selection-read" },
    );
    return SLIDE_SNAPSHOT;
  }
}
